import { type FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';

import { AppRoute } from '@/common/app/routes';
import { H3, TextSmall } from '@/components/Typography/Typography';
import { MainButton, SecondaryButton } from '@/components/ui/Button/Button';
import { Checkbox } from '@/components/ui/Checkbox/Checkbox';
import { Picture } from '@/components/ui/Picture/Picture';
import { setStockOverflowNotificationIsOpen } from '@/redux/reducers/modalsReducer';
import { useAppDispatch, useAppSelector } from '@/redux/store';

import css from './styles.module.scss';

import warningPng from '/img/warning.png';
import warningWebp from '/img/warning.webp';

const StockOverflow: FC = () => {
  const isOpen = useAppSelector(
    (state) => state.modals.stockOverflowNotificationIsOpen,
  );
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [hiddenChecked, setHiddenChecked] = useState(false);

  const handleCloseNotification = (): void => {
    if (hiddenChecked) {
      localStorage.setItem(
        'stockOverflowModalHiddenDate',
        new Date().toLocaleDateString(),
      );
    }
    dispatch(setStockOverflowNotificationIsOpen(false));
  };

  useEffect(() => {
    if (!isOpen) {
      setHiddenChecked(false);
    }
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className={css.stockOverflowWrapper}
        >
          <div className={css.stockOverflow}>
            <div className="w-full h-full flex flex-col items-center relative z-[2] gap-2">
              <Picture
                className="w-[5.5rem]"
                img={warningPng}
                webp={warningWebp}
                alt="warning"
              />
              <H3 weight="bold" className="-mt-[13px]">
                {t('Attention: the stock is not infinite!')}
              </H3>
              <TextSmall className="!leading-1.2 !text-white/80 text-center">
                {t(
                  'Some of your supplies exceed stock capacity and are temporarily stored outside. Make sure to process or sell them in time — storage outside the warehouse is not guaranteed.',
                )}
              </TextSmall>
              <Checkbox
                onChange={() => setHiddenChecked((state) => !state)}
                checked={hiddenChecked}
                variant="default"
                classNames={{
                  label: 'flex items-center gap-2 mt-4 mb-0.5',
                }}
                label={() => (
                  <TextSmall className="!leading-1.2 !text-white/80 text-center">
                    {t('Dont show today')}
                  </TextSmall>
                )}
              />
              <div className="flex w-full gap-4 pt-[1.125rem]">
                <SecondaryButton
                  onClick={handleCloseNotification}
                  className="flex-1 !w-auto"
                  text="OK"
                />
                <MainButton
                  className="flex-1 !w-auto"
                  onClick={() => {
                    handleCloseNotification();
                    navigate(AppRoute.STOCK);
                  }}
                  text={t('Manage Stock')}
                />
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export { StockOverflow };
